/**
 * tableFacetCounts — glanceable per-facet counts for the compact `TableEditor`
 * tab labels (`Columns (n)` / `Constraints (n)` / `Indexes (n)`). Pure data
 * adapter over `DdlStructure`, no React.
 *
 * Only facets whose data is REAL get a count — a label that always reads `(0)`
 * lies about the object:
 *   - columns → `columnRowsFromStructure()` (the ColumnsTab row adapter).
 *   - indexes → `indexRowsFromStructure()` (shared with `IndexesTab`).
 *   - constraints / storage → NO count. `DdlStructure` carries no structured
 *     constraint list (table-level constraints land inside
 *     `create-table.opaqueRanges` / `alter-table`'s `opaque` clauses) and
 *     `ConstraintsTab` correspondingly renders add/drop affordances only, so
 *     `TableEditor` renders those two tabs as plain labels. When the DSE2
 *     Phase B constraint extractor lands, add a `constraints` field here and
 *     light the label up.
 *
 * Total by design: every arm is exhaustively defaulted, so the helper is safe
 * on ANY `DdlStructure` (including the seed-only grant/type/trigger/routine
 * arms) and on `null` / `undefined`.
 *
 * Redesign: .superpowers/sdd/2026-07-28-compact-visual-ddl-editor/
 */
import type { DdlStructure, IndexDef } from '../../../../sql-resolver/types/ddlEdit';

import { columnRowsFromStructure } from './columnRows';

/** Index rows a `DdlStructure` exposes, followed by `documentIndexes` — the
 *  indexes of the CREATE INDEX statements that share the document with the
 *  statement (the Table Designer's buffer spells a table's indexes that way,
 *  after its CREATE TABLE). Only the `create-index` arm itself carries a
 *  structured `IndexDef`; every other arm (including `create-table`, whose
 *  inline index declarations are parked as opaque ranges) yields none of its
 *  own. */
export function indexRowsFromStructure(
  structure: DdlStructure | null | undefined,
  documentIndexes: readonly IndexDef[] = [],
): readonly IndexDef[] {
  const own = structure?.kind === 'create-index' ? [structure.index] : [];
  return documentIndexes.length === 0 ? own : [...own, ...documentIndexes];
}

export interface TableFacetCounts {
  readonly columns: number;
  readonly indexes: number;
}

export function tableFacetCounts(
  structure: DdlStructure | null | undefined,
  documentIndexes: readonly IndexDef[] = [],
): TableFacetCounts {
  return {
    columns: columnRowsFromStructure(structure).length,
    indexes: indexRowsFromStructure(structure, documentIndexes).length,
  };
}

const CHANGE_ORDER = ['added', 'dropped', 'renamed'] as const;

/**
 * The compact `Columns` tab label. A CREATE TABLE lists the table's columns,
 * so the count is the table's (`Columns (3)`). An ALTER TABLE lists only the
 * columns the statement changes — a bare `Columns (1)` there read as "this
 * table has one column" — so the label names the change instead:
 * `Columns (1 added)`, `Columns (1 added, 2 dropped)` — the same parenthesised
 * form as `Indexes (0)` and the CREATE count. An ALTER that touches
 * no column (constraints only) is just `Columns`.
 */
export function compactColumnsTabLabel(structure: DdlStructure | null | undefined): string {
  const rows = columnRowsFromStructure(structure);
  if (structure?.kind !== 'alter-table') return `Columns (${rows.length})`;
  const parts = CHANGE_ORDER.flatMap((action) => {
    const n = rows.filter((row) => row.action === action).length;
    return n > 0 ? [`${n} ${action}`] : [];
  });
  return parts.length > 0 ? `Columns (${parts.join(', ')})` : 'Columns';
}
